import { useCallback, useEffect, useState } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';

// -- type definitions --

export interface UseImageGalleryResourceIdsResult {
  readonly resourceIds: readonly number[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly reload: () => Promise<void>;
}

// -- helper functions --

/**
 * Resolves gallery tag names to resource ids using the global tag catalog.
 */
export function resolveImageGalleryResourceIds(
  tagNames: readonly string[],
  tagCatalog: ReadonlyArray<{ readonly id: number; readonly name: string }>,
  resourceTags: ReadonlyArray<{
    readonly resource_id: number;
    readonly tag_id: number;
  }>
): number[] {
  if (tagNames.length === 0) {
    return [];
  }

  const tagNameSet = new Set(tagNames);
  const tagIds = new Set(
    tagCatalog
      .filter((tag) => tagNameSet.has(tag.name))
      .map((tag) => tag.id)
  );

  return [
    ...new Set(
      resourceTags
        .filter((entry) => tagIds.has(entry.tag_id))
        .map((entry) => entry.resource_id)
    ),
  ];
}

// -- main hook --

/**
 * Loads resource ids for an image gallery widget from host tag APIs.
 */
export function useImageGalleryResourceIds(
  _projectId: number,
  tagNames: readonly string[]
): UseImageGalleryResourceIdsResult {
  const host = useDashboardHost();
  const [resourceIds, setResourceIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tagNamesKey = tagNames.join('\0');

  const reload = useCallback(async () => {
    const resolvedTagNames =
      tagNamesKey.length === 0 ? [] : tagNamesKey.split('\0');

    if (resolvedTagNames.length === 0) {
      setResourceIds([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [tagCatalog, resourceTags] = await Promise.all([
        host.api.tag.getAll(),
        host.api.tag.getAllResourcesTags(),
      ]);

      setResourceIds(
        resolveImageGalleryResourceIds(
          resolvedTagNames,
          tagCatalog,
          resourceTags
        )
      );
    } catch (loadError) {
      setResourceIds([]);
      setError(
        loadError instanceof Error ? loadError : new Error(String(loadError))
      );
    } finally {
      setLoading(false);
    }
  }, [host.api.tag, tagNamesKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { resourceIds, loading, error, reload };
}
